// src/pages/dashboard/admin/ProductDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Package,
  CheckCircle,
  XCircle,
  Clock,
  Edit,
  Download,
  Printer,
  Mail,
  Calendar,
  Tag,
  Box,
  DollarSign,
  Hash,
  FileText,
  AlertCircle,
  User,
  Building,
  PhoneCall
} from 'lucide-react';
import Button from '../../../../components/common/Button';
import Card from '../../../../components/ui/Card';
import Badge from '../../../../components/Badge'; // Fixed import
import Toast from '../../../../components/Toast';
import AdminProductService from '../admin.product.service';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  useEffect(() => {
    if (id) {
      fetchProduct();
    }
  }, [id]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      console.log(`📋 Fetching product details for ID: ${id}`);
      const productData = await AdminProductService.getProductById(id);
      setProduct(productData);
    } catch (error) {
      console.error('❌ Error fetching product:', error);
      setToastMessage(`Failed to load product: ${error.message}`);
      setToastType('error');
      setShowToast(true);
      setTimeout(() => navigate('/admin/products'), 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!window.confirm('Are you sure you want to approve this product?')) return;
    
    try {
      await AdminProductService.approveProduct(id);
      setToastMessage('✅ Product approved successfully!');
      setToastType('success');
      setShowToast(true);
      fetchProduct();
    } catch (error) {
      console.error('Error approving:', error);
      setToastMessage(`❌ Failed to approve: ${error.message}`);
      setToastType('error');
      setShowToast(true);
    }
  };

  const handleReject = async () => {
    const reason = window.prompt('Please enter rejection reason:');
    if (!reason || !reason.trim()) {
      setToastMessage('Rejection reason is required');
      setToastType('error');
      setShowToast(true);
      return;
    }
    
    try {
      await AdminProductService.rejectProduct(id, reason);
      setToastMessage('✅ Product rejected successfully!');
      setToastType('success');
      setShowToast(true);
      fetchProduct();
    } catch (error) {
      console.error('Error rejecting:', error);
      setToastMessage(`❌ Failed to reject: ${error.message}`);
      setToastType('error');
      setShowToast(true);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure? This action cannot be undone.')) return;
    
    try {
      await AdminProductService.deleteProduct(id);
      setToastMessage('✅ Product deleted successfully!');
      setToastType('success');
      setShowToast(true);
      setTimeout(() => navigate('/admin/products'), 1500);
    } catch (error) {
      console.error('Error deleting:', error);
      setToastMessage(`❌ Failed to delete: ${error.message}`);
      setToastType('error');
      setShowToast(true);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING':
        return (
          <Badge variant="warning" size="md">
            <Clock className="h-4 w-4 mr-2" />
            Pending Review
          </Badge>
        );
      case 'APPROVED':
        return (
          <Badge variant="success" size="md">
            <CheckCircle className="h-4 w-4 mr-2" />
            Approved
          </Badge>
        );
      case 'REJECTED':
        return (
          <Badge variant="danger" size="md">
            <XCircle className="h-4 w-4 mr-2" />
            Rejected
          </Badge>
        );
      default:
        return <Badge variant="gray" size="md">{status}</Badge>;
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading product details...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <div className="p-8">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Product Not Found</h2>
            <p className="text-gray-600 mb-6">The requested product could not be found.</p>
            <Button 
              variant="primary" 
              onClick={() => navigate('/admin/products')}
              className="w-full"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Products
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Toast */}
      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
        />
      )}

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="small"
            onClick={() => navigate('/admin/products')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Products
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{product.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-gray-500">Product ID:</span>
              <code className="text-xs bg-gray-100 px-2 py-1 rounded">{id}</code>
            </div>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {product.status === 'PENDING' && (
            <>
              <Button variant="success" onClick={handleApprove}>
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve
              </Button>
              <Button variant="danger" onClick={handleReject}>
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>
            </>
          )}
          
          {/* <Button variant="outline" onClick={() => navigate(`/admin/products/${id}/edit`)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button> */}
          
          <Button variant="warning" onClick={handleDelete}>
            <FileText className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status Card */}
          <Card>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  {getStatusBadge(product.status)}
                  {product.status === 'REJECTED' && product.rejectionReason && (
                    <div className="text-sm text-red-600">
                      <AlertCircle className="h-4 w-4 inline mr-1" />
                      {product.rejectionReason}
                    </div>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  Updated: {formatDate(product.updatedAt)}
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <Button variant="outline" size="small">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
                <Button variant="outline" size="small">
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
              </div>
            </div>
          </Card>

          {/* Images */}
          <Card>
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Package className="h-5 w-5 mr-2" />
              Product Images
            </h3>
            {product.images && product.images.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {product.images.map((img, index) => (
                  <div key={index} className="aspect-square border rounded-lg overflow-hidden">
                    <img 
                      src={img.data || img.url || `https://via.placeholder.com/300`}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <Package className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No images available</p>
              </div>
            )}
          </Card>

          {/* Description */}
          <Card>
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Product Description
            </h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-600 whitespace-pre-line">
                {product.description || 'No description provided.'}
              </p>
            </div>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Product Info */}
          <Card>
            <h3 className="text-lg font-semibold mb-4">Product Information</h3>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-500 mb-1">Category</div>
                <div className="flex items-center">
                  <Tag className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="font-medium">{product.category || 'N/A'}</span>
                </div>
              </div>
              
              <div>
                <div className="text-sm text-gray-500 mb-1">HS Code</div>
                <div className="flex items-center">
                  <Hash className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="font-medium">{product.hsCode || 'N/A'}</span>
                </div>
              </div>
              
              <div>
                <div className="text-sm text-gray-500 mb-1">Quantity</div>
                <div className="flex items-center">
                  <Box className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="font-medium">
                    {product.quantity || '0'} {product.unit || ''}
                  </span>
                </div>
              </div>
              
              <div>
                <div className="text-sm text-gray-500 mb-1">Price</div>
                <div className="flex items-center">
                  <DollarSign className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="font-medium">
                    {product.currency || '₹'}{product.price || '0'}
                  </span>
                </div>
              </div>
              
              <div>
                <div className="text-sm text-gray-500 mb-1">Created</div>
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="font-medium">
                    {formatDate(product.createdAt)}
                  </span>
                </div>
              </div>
            </div>
          </Card>

          {/* Seller Info */}
          <Card>
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <User className="h-5 w-5 mr-2" />
              Seller Information
            </h3>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-500 mb-1">Name</div>
                <div className="flex items-center">
                  <User className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="font-medium">{product.sellerName || 'N/A'}</span>
                </div>
              </div>
              
              <div>
                <div className="text-sm text-gray-500 mb-1">Email</div>
                <div className="flex items-center">
                  <Mail className="h-4 w-4 text-gray-400 mr-2" />
                  <a 
                    href={`mailto:${product.sellerEmail}`}
                    className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {product.sellerEmail || 'N/A'}
                  </a>
                </div>
              </div>
              
              {product.sellerPhone && (
                <div>
                  <div className="text-sm text-gray-500 mb-1">Phone</div>
                  <div className="flex items-center">
                    <PhoneCall className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="font-medium">{product.sellerPhone}</span>
                  </div>
                </div>
              )}
              
              {product.sellerCompany && (
                <div>
                  <div className="text-sm text-gray-500 mb-1">Company</div>
                  <div className="flex items-center">
                    <Building className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="font-medium">{product.sellerCompany}</span>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Admin Notes */}
          {(product.approvedByEmail || product.rejectionReason) && (
            <Card>
              <h3 className="text-lg font-semibold mb-4">Admin Notes</h3>
              <div className="space-y-3">
                {product.approvedByEmail && (
                  <div className="p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                      <div>
                        <div className="font-medium text-green-800">Approved by {product.approvedByEmail}</div>
                        {product.approvedAt && (
                          <div className="text-xs text-green-600">
                            {formatDate(product.approvedAt)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {product.rejectionReason && (
                  <div className="p-3 bg-red-50 rounded-lg">
                    <div className="flex items-center">
                      <XCircle className="h-5 w-5 text-red-600 mr-2" />
                      <div>
                        <div className="font-medium text-red-800">Rejected</div>
                        <div className="text-sm text-red-600">{product.rejectionReason}</div>
                        {product.rejectedAt && (
                          <div className="text-xs text-red-500">
                            {formatDate(product.rejectedAt)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;